import { z } from 'zod';

import { AccountMarkets, AccountMarket } from '..';

export const FEES_FRACTION = 0.2;

/**
 * Configuration for a single market's audience.
 */
export interface MarketAudienceConfig {
  /**
   * Retailer IDs in this market
   * - [] = all retailers in market
   * - ['id1', 'id2'] = specific retailers only
   */
  retailers: string[];

  /**
   * Location overrides for specific retailers (sparse)
   * - Key absent = all locations at retailer
   * - ['loc1'] = specific locations only
   *
   * Empty arrays are normalized away on save.
   */
  locationOverrides?: Record<string, string[]>;
}

/**
 * Custom audience configuration for vendor courses.
 * Keys are market codes. Only markets with keys are targeted.
 *
 * When present (not null/undefined), the course is in "custom mode":
 * When absent (null/undefined), standard "markets mode" applies:
 */
export type CourseRetailerAudience = Record<string, MarketAudienceConfig>;

export const marketAudienceConfigSchema = z.object({
  retailers: z.array(z.string()),
  locationOverrides: z.record(z.string(), z.array(z.string())).optional(),
});

export const courseRetailerAudienceSchema = z.record(z.string(), marketAudienceConfigSchema);

/**
 * Normalizes custom audience by removing empty locationOverrides arrays.
 * This keeps the document sparse - only retailers with explicit location
 * filtering have entries in locationOverrides.
 */
export function normalizeCustomAudience(
  input: CourseRetailerAudience | null | undefined,
): CourseRetailerAudience | null {
  if (!input) {
    return null;
  }

  const result: CourseRetailerAudience = {};

  for (const [market, config] of Object.entries(input)) {
    const normalized: MarketAudienceConfig = {
      retailers: config.retailers,
    };

    if (config.locationOverrides) {
      const sparse: Record<string, string[]> = {};
      for (const [retailerId, locs] of Object.entries(config.locationOverrides)) {
        if (locs.length > 0) {
          sparse[retailerId] = locs;
        }
      }
      if (Object.keys(sparse).length > 0) {
        normalized.locationOverrides = sparse;
      }
    }

    result[market] = normalized;
  }

  return Object.keys(result).length > 0 ? result : null;
}

export const LEARNING_RESOURCE_REWARD_TYPES = ['cash', 'custom'] as const;
export type LearningResourceRewardType = typeof LEARNING_RESOURCE_REWARD_TYPES[number];

export const LEARNING_RESOURCE_FULFILLMENT_TYPES = ['sparkplug', 'external'] as const;
export type LearningResourceFulfillmentType = typeof LEARNING_RESOURCE_FULFILLMENT_TYPES[number];

export interface ILearningResource {
  learningResourceId: string;
  status: LearningResourceStatus;
  accountId: string;
  accountName?: string;
  accountType?: string;
  accountPhoto?: string | null;
  surveyId?: string | null;
  surveyParticipants?: number; // count of survey responses
  employeeDepositsCount?: number; // count of employee deposits
  totalPayout?: number; // total payout
  brandId?: string | null;
  brandName?: string;
  brandPhoto?: string | null;

  markets: AccountMarket[];
  locationIds?: string[];
  description: string;
  name: string;
  reward: number;
  budget?: number;
  fulfillmentType: LearningResourceFulfillmentType;
  rewardName?: string;
  rewardDescription?: string;
  claimInstructions?: string;
  customRewardTermsAccepted?: boolean;
  rewardCompletionGoal?: number;
  videoUrl?: string | null;
  imageUrl?: string | null;
  createdBy: string;
  createdByFirstName?: string;
  createdByLastName?: string;
  createdAt?: Date | null;
  updatedAt?: Date | null;
  launchedAt?: Date | null;
  removedAt?: Date | null;

  internalNotes?: string | null;
  surveyData?: Record<string, unknown> | null;
  themeData?: Record<string, unknown> | null;
  generationMetadata?: GenerationMetadataInput | null;
  billingMode?: 'auto' | 'manual';

  surveyParticipantsCount?: number | null;
  currentExpenditure?: number | null;
  lastWebPushNotificationSentAt?: Date | null;
  lastWebPushNotificationScheduledAt?: Date | null;

  /**
   * Custom audience configuration for vendor courses.
   * null/undefined = standard mode
   */
  customAudience?: CourseRetailerAudience | null;
}
export interface IEmployeeLearningResource extends ILearningResource {
  surveyResponse?: Record<string, unknown>;
}

export const generationMetadataSchema = z.object({
  prompt: z.string(),
  templateId: z.string().optional(),
  model: z.string(),
  tokenUsage: z.object({
    input: z.number(),
    output: z.number(),
  }),
  generatedAt: z.string().transform((str) => new Date(str)),
  generationDurationMs: z.number(),
  iterations: z.object({
    count: z.number(),
    maxAllowed: z.number(),
    errors: z.array(z.string()).optional(),
  }),
});

export type GenerationMetadataInput = z.infer<typeof generationMetadataSchema>;

export const updateLearningResourceSchema = z.object({
  markets: z.array(z.enum(AccountMarkets as [string, ...string[]])).optional(),
  locationIds: z.array(z.string()).optional(),
  description: z.string().optional(),
  name: z.string().optional(),
  reward: z.number().optional(),
  videoUrl: z.string().optional(),
  imageUrl: z.string().optional(),
  accountId: z.string().optional(),
  brandId: z.string().optional(),
  launchedAt: z
    .string()
    .transform((str) => (str ? new Date(str) : null))
    .optional(),
  removedAt: z
    .string()
    .transform((str) => (str ? new Date(str) : null))
    .optional(),
  surveyData: z.record(z.string(), z.unknown()).optional(),
  themeData: z.record(z.string(), z.unknown()).optional(),
  internalNotes: z.string().optional(),
  billingMode: z.enum(['auto', 'manual']).optional(),
  budget: z.number().optional(),
  fulfillmentType: z.enum(LEARNING_RESOURCE_FULFILLMENT_TYPES).default('sparkplug'),
  rewardName: z.string().optional(),
  rewardDescription: z.string().optional(),
  claimInstructions: z.string().optional(),
  customRewardTermsAccepted: z.boolean().optional(),
  rewardCompletionGoal: z.number().optional(),
  customAudience: courseRetailerAudienceSchema.nullable().optional(),
  generationMetadata: generationMetadataSchema.optional(),
});

export const createLearningResourceSchema = z.object({
  accountId: z.string(),
  surveyId: z.string().optional(),
  brandId: z.string().optional(),
  markets: z.array(z.enum(AccountMarkets as [string, ...string[]])),
  locationIds: z.array(z.string()).optional(),
  description: z.string().optional(),
  name: z.string().optional(),
  reward: z.number(),
  videoUrl: z.string().optional(),
  imageUrl: z.string().optional(),
  launchedAt: z
    .string()
    .transform((str) => (str ? new Date(str) : null))
    .optional(),
  surveyData: z.record(z.string(), z.unknown()).optional(),
  themeData: z.record(z.string(), z.unknown()).optional(),
  internalNotes: z.string().optional(),
  billingMode: z.enum(['auto', 'manual']).optional(),
  budget: z.number().optional(),
  fulfillmentType: z.enum(LEARNING_RESOURCE_FULFILLMENT_TYPES).default('sparkplug'),
  rewardName: z.string().optional(),
  rewardDescription: z.string().optional(),
  claimInstructions: z.string().optional(),
  customRewardTermsAccepted: z.boolean().optional(),
  rewardCompletionGoal: z.number().optional(),
  customAudience: courseRetailerAudienceSchema.nullable().optional(),
  generationMetadata: generationMetadataSchema.optional(),
});

export interface CourseInvoice {
  id: string;
  amount: number;
  status: string | 'unknown';
  created: number;
  paid: boolean;
  hostedInvoiceUrl?: string | null;
}

export interface GetCourseBillingSummaryResponse {
  invoices: CourseInvoice[];
  billedAmount: number;
  billedCount: number;
  unbilledAmount: number;
  unbilledCount: number;
  totalAmount: number;
  totalCount: number;
}

export type CreateLearningResourceRequestBody = z.infer<typeof createLearningResourceSchema>;
export type UpdateLearningResourceRequestBody = z.infer<typeof updateLearningResourceSchema>;
export type CreateLearningResourceResponseBody = ILearningResource;
export type GetLearningResourceResponseBody = ILearningResource;

export interface DeleteLearningResourcePathParams {
  learningResourceId: string;
}

export interface GetLearningResourcePathParams {
  learningResourceId: string;
}

export interface UpdateLearningResourcePathParams {
  learningResourceId: string;
}

export const LearningResourceStatuses = ['published', 'unpublished', 'draft'] as const;
export type LearningResourceStatus = typeof LearningResourceStatuses[number];

export const listLearningResourceRequestParamsSchema = z.object({
  brandId: z.union([z.string(), z.array(z.string())]).optional(),
  accountId: z.union([z.string(), z.array(z.string())]).optional(),
  status: z.enum(LearningResourceStatuses).optional(),
  limit: z.string().optional(),
  billingMode: z.enum(['auto', 'manual']).optional(),
  market: z.enum(AccountMarkets as [string, ...string[]]).optional(),
  offset: z.string().optional(),
  sort: z.string().optional(),
  order: z.enum(['asc', 'desc']).optional(),
  q: z.string().optional(),
  locationIds: z.union([z.string(), z.array(z.string())]).optional(),
  launchedAtStart: z.string().optional(),
  launchedAtEnd: z.string().optional(),
});

export const listLearningResourceResponseRequestParamsSchema = z.object({
  limit: z.string().optional(),
  offset: z.string().optional(),
  sort: z.string().optional(),
  order: z.enum(['asc', 'desc']).optional(),
});

export type GetAllLearningResourceQueryParams = z.infer<
  typeof listLearningResourceRequestParamsSchema
>;

export const availableAudienceQuerySchema = z.object({
  brandId: z.string(),
});

export type AvailableAudienceQueryParams = z.infer<typeof availableAudienceQuerySchema>;

export interface AudienceLocationInfo {
  id: string;
  name: string;
  employeeCount: number;
}

export interface AudienceRetailerInfo {
  id: string;
  name: string;
  photo?: string;
  locations: AudienceLocationInfo[];
}

export interface InactiveRetailerInfo {
  id: string;
  name: string;
  photo?: string;
  reason: string;
}

export interface AvailableAudienceResponseBody {
  markets: Record<
    string,
    {
      retailers: AudienceRetailerInfo[];
      inactiveRetailers?: InactiveRetailerInfo[];
    }
  >;
}

export const MAX_COURSE_PROMPT_LENGTH = 5000;

export const generateCourseRequestSchema = z.object({
  prompt: z
    .string()
    .min(1, 'Prompt is required')
    .max(
      MAX_COURSE_PROMPT_LENGTH,
      `Prompt must be ${MAX_COURSE_PROMPT_LENGTH} characters or fewer`,
    ),
  templateId: z.string().optional(),
});

export type GenerateCourseRequestBody = z.infer<typeof generateCourseRequestSchema>;

export interface GenerateCourseResponseBody {
  courseName: string;
  courseDescription: string;
  surveyData: Record<string, unknown>;
  tokenUsage: {
    input: number;
    output: number;
  };
  model: string;
  templateId: string | null;
  generationDurationMs: number;
  iterations: {
    count: number;
    maxAllowed: number;
    errors?: string[];
  };
}
